<template>
  <div>
    <sidebar-form
      :visible="showForm"
      :resource-id="resourceId"
      :status-options="statusOptions"
      @saved="getDetails()"
      @close="onCloseForm"
    />

    <prozess-resource-view
      table-name="contact"
      :resource="resource"
      :loading="loading"
      :fields="fields"
      :custom-fields="customFields"
      :status-options="statusOptions"
      :can-update="$can('Update', 'CRM')"
      @edit="$can('Update', 'CRM') ? edit(resource.uuid) : null"
      @relationshipsLoaded="handleRelationshipsLoaded"
    >
      <template slot="body">
        <prozess-tab-loader v-if="relationshipsLoading" />
        <b-tabs
          v-else
          content-class="mt-1"
          :value="tabs.indexOf(this.$route.params.tab)"
          @input="handleTabInput"
        >
          <b-tab>
            <template #title> <feather-icon icon="GridIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('360 Overview') }}</p></template>
            <EntityOverviewWidgets
              :resource="resource"
              :field-labels="fields"
              schema-key="crm"
              table-key="contact"
            />
          </b-tab>
          <b-tab v-if="this.$can('Read', 'Dashboard')">
            <template #title> <feather-icon icon="TrendingUpIcon" /> <p style="font-weight: 600; margin:0px;">{{ $t('Dashboard') }}</p></template>
            <dashboard-tab />
          </b-tab>
          <b-tab>
            <template #title> <feather-icon icon="ActivityIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Activities') }}</p></template>
            <ActivityList />
          </b-tab>
          <b-tab :title="$t('Commissions')">
            <template #title> <feather-icon icon="CircleIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Commissions') }}</p></template>
            <Commissions />
          </b-tab>
          <b-tab>
            <template #title> <feather-icon icon="BriefcaseIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Companies') }}</p></template>
            <company-list :status-options="statusOptions" />
          </b-tab>
          <b-tab>
            <template #title> <feather-icon icon="UsersIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Contacts') }}</p></template>
            <CRMRelationshipMappingList
              :type="Entity.CONTACT"
              :entity-id="$route.params.id"
              :resource-name="`${resource.firstName || ''} ${resource.lastName || ''}`"
            />
          </b-tab>
          <b-tab v-if="this.$can('Read', 'Document')" :title="$t('Documents')">
            <template #title> <feather-icon icon="CheckSquareIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Documents') }}</p></template>
            <document-list
              entity-type="contact"
              :entity-id="$route.params.id"
            />
          </b-tab>
          <b-tab :title="$t('History')">
            <template #title> <feather-icon icon="ListIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('History') }}</p></template>
            <b-overlay
              :show="loading"
              spinner-variant="primary"
              rounded="sm"
              variant="transparent"
              style="height: 100%"
              class="d-flex flex-column"
            >
              <div class="card">
                <div class="card-body mb-5">
                  <prozess-resource-history
                    :history="items"
                    :loading="loading"
                    :history-pagination="{
                      currentPage,
                      total,
                      perPage,
                    }"
                    @handleCurrentPage="handleCurrentPage"
                  />
                </div>
              </div>
            </b-overlay>
          </b-tab>
          <b-tab v-if="this.$can('Read', 'Inquiry')" :title="$t('Inquiries')">
            <template #title> <feather-icon icon="HelpCircleIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Inquiries') }}</p></template>
            <inquiries
              :entity-type="Entity.CONTACT"
              :entity-id="$route.params.id"
            />
          </b-tab>
          <b-tab v-if="this.$can('Read', 'Note')" >
            <template #title> <feather-icon icon="ClipboardIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Notes') }}</p></template>
            <notes
              entity-type="contact"
              :entity-id="$route.params.id"
            />
          </b-tab>
          <b-tab :title="$t('Sales')">
            <template #title> <feather-icon icon="CircleIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('Sales') }}</p></template>
            <Sales />
          </b-tab>
          <b-tab v-if="this.$can('Read', 'Todo')" :title="$t('To Do')">
            <template #title> <feather-icon icon="ListIcon" /><p style="font-weight: 600; margin:0px;">{{ $t('To Do') }}</p></template>
            <todo
              :entity-type="Entity.CONTACT"
              :entity-id="$route.params.id"
            />
          </b-tab>
          <b-tab
            v-for="relationship of shownRelationships"
            :key="relationship.key2"
          >
            <template #title> <feather-icon icon="CircleIcon" />{{ $t(relationship.label2) }}</template>
            <x-to-many-list
              owning-entity-type="contact"
              :owning-entity-id="$route.params.id"
              :owning-schema="Schema.CRM"
              :related-entity-type="relationship.key2"
              :related-schema="relationship.schema2"
              :related-entity-id="relationship.id"
              :visibility="relationship.visibility2"
            />
          </b-tab>
        </b-tabs>
      </template>
    </prozess-resource-view>
  </div>
</template>

<script>
import { Schema, Entity, GenericTabs } from '@/constants/app'
import { tabKeys, visibleRelationships } from '@/helpers/entityTabs'
import contactService from '@/services/contact'
import listMixinFactory from '@/mixins/list'
import RootEvents from '@/constants/rootEvents'
import SidebarForm from '@/components/Contact/ContactForm.vue'
import CompanyList from '@/views/pages/Contacts/CompanyList.vue'
import DocumentList from '@/views/pages/Documents/ListOnView/index.vue'
import Notes from '@/views/pages/Notes/ListOnView/index.vue'
import ProzessTabLoader from '@/@core/components/ProzessTabLoader.vue'
import XToManyList from '@/views/pages/CustomEntity/List/XToManyList.vue'
import Inquiries from '@/views/pages/Inquiries/ListOnView/index.vue'
import Todo from '@/views/pages/Todo/ListOnView/index.vue'
import CRMRelationshipMappingList from '@/components/CRMRelationships/CRMRelationshipMappingList.vue'
import EntityOverviewWidgets from '@/components/EntityOverview/EntityOverviewWidgets.vue'
import ActivityList from '@/components/Activity/ActivityList.vue'
import CustomFieldTypes from '@/constants/customFieldTypes'
import executeQueryFieldMixin from '@/mixins/executeQueryField'
import metastatusDropdownMixin from '@/mixins/metastatusDropdown'
import Sales from '@/views/pages/Sales/Seller/ListOnView/index.vue'
import Commissions from '@/views/pages/Commissions/ListOnView/View.vue'
import DashboardTab from '@/views/pages/DashboardTab.vue'
import { mapGetters, mapActions, mapState } from 'vuex'
import employeeService from '@/services/employee'

// The fixed tabs in the order the template draws them. A `permission` mirrors the tab's v-if.
const FIXED_TABS = [
  { key: GenericTabs.OVERVIEW_360 },
  { key: GenericTabs.DASHBOARD, permission: ['Read', 'Dashboard'] },
  { key: GenericTabs.ACTIVITIES },
  { key: GenericTabs.COMMISSIONS },
  { key: GenericTabs.COMPANIES },
  { key: GenericTabs.CONTACTS },
  { key: GenericTabs.DOCUMENTS, permission: ['Read', 'Document'] },
  { key: GenericTabs.HISTORY },
  { key: GenericTabs.INQUIRIES, permission: ['Read', 'Inquiry'] },
  { key: GenericTabs.NOTES, permission: ['Read', 'Note'] },
  { key: GenericTabs.SALES },
  { key: GenericTabs.TODO, permission: ['Read', 'Todo'] },
]

export default {
  components: {
    XToManyList,
    ProzessTabLoader,
    SidebarForm,
    CompanyList,
    DocumentList,
    Notes,
    Inquiries,
    Todo,
    CRMRelationshipMappingList,
    EntityOverviewWidgets,
    ActivityList,
    Sales,
    Commissions,
    DashboardTab,
  },
  mixins: [
    metastatusDropdownMixin,
    listMixinFactory({
      service: contactService,
      isHistory: true,
    }),

    executeQueryFieldMixin({
      schemaKey: Schema.CRM,
      tableKey: Entity.CONTACT,
    }),
  ],
  data() {
    return {
      Entity,
      Schema,
      resource: {
        customFields: {},
      },
      loading: false,
      fields: {
        firstName: 'First Name',
        lastName: 'Last Name',
        mobileNumber: 'Mobile',
        emailAddress: 'Email Address',
        title: 'Job Title',
        birthDate: 'Birth Date',
        address: 'Address',
        city: 'City',
        zip: 'Zip',
        country: 'Country',
        description: 'Description',
      },
      customFields: {},
      relationships: [],
      relationshipsLoading: true,
      userEmployee: {},
    }
  },
  computed: {
    // The drawn relationship tabs and the URL tab keys both come from here, so hiding a
    // relationship from the viewer cannot shift one against the other. Recomputed when the
    // viewer's groups arrive, which can be after the relationships have.
    shownRelationships() {
      return visibleRelationships(this.relationships, {
        isCompanyAdmin: this.$can('PerformAs', 'CompanyAdmin'),
        groupIds: (this.userEmployee.groups || []).map(grp => grp.groupId),
      })
    },
    tabs() {
      return tabKeys(FIXED_TABS, (action, subject) => this.$can(action, subject), this.shownRelationships)
    },
    ...mapState('user', ['user']),
    ...mapGetters('user'),
  },

  watch: {
    '$route.params.id': {
      async handler() {
        this.getDetails()
        this.getMetastatusInitialData()
        const userDataRes = await employeeService.getUserEmployee(this.user.userUuid)
        this.userEmployee = userDataRes.data
      },

      immediate: true,
    },
  },

  methods: {
    handleRelationshipsLoaded(value) {
      this.relationships = value.filter(item => item.schema2 !== 'crm')
      this.relationships = this.relationships.filter(item => !(item.schema2 === 'custom_schema' && item.key2 === 'salg'))
      this.relationships = this.relationships.filter(item => !(item.schema2 === 'custom_schema' && item.key2 === 'salg_et_400'))
       this.relationships.sort((a, b) => a.label2.toLowerCase().localeCompare(b.label2.toLowerCase()))
      this.relationshipsLoading = false
    },

    handleTabInput(index) {
      this.$router
        .push({
          name: 'contact-view',
          params: {
            idFeed: this.$route.params.idFeed,
            id: this.$route.params.id,
            tab: this.tabs[index] || null,
          },
        })
        .catch(() => {})
    },

    getDetails() {
      return contactService.getOne(this.$route.params.id).then(res => {
        const sortedCustomFields = res.data.customFieldMetadata.sort((a, b) => b.favorite - a.favorite)

        this.customFields = sortedCustomFields.reduce((acc, field) => {
          acc[field.key] = field
          return acc
        }, {})

        const customFields = {
          ...sortedCustomFields.reduce((acc, customField) => {
            const result = res.data.customFieldData.find(data => customField.id === data.metadataId)

            // This is needed for prefilling query fields. See: src/mixins/executeQueryField.js
            if (customField.fieldType === CustomFieldTypes.Query) {
              this.queryFields.push(customField)
              this.$lodash.set(result, 'value', 'Querying...')
            }

            if (result) {
              acc[customField.key] = result.value
            }
            return acc
          }, {}),
        }

        this.resource = {
          ...res.data,
          customFields,
        }

        this.$lodash.invoke(this, 'prefillQueryFields')

        this.$root.$emit(RootEvents.SET_PAGE_TITLE, `${this.resource.firstName || ''} ${this.resource.lastName}`)
      })
    },
  },
}
</script>
